/**
 * PayoffWinDialog — Phase 86.E Pass B, "Payoff wins".
 *
 * Fires once when a debt crosses 50% / 75% / 90% repaid, and again when it's
 * fully paid off. Driven by `plan.milestones` from the canonical model, so a
 * celebration can never appear for a debt whose progress we can't prove.
 * Acknowledgement is remembered per debt + stage, so it never nags twice.
 * The shareable line deliberately carries no amounts.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { PartyPopper, Share2, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { formatMoney } from '@/lib/format';
import { useAuth } from '@/context/AuthContext';

const STAGE = {
  cleared: { rank: 4, title: 'Paid off', line: 'This debt is fully cleared.' },
  final_stretch: { rank: 3, title: 'Final stretch', line: 'More than 90% of it is behind you.' },
  three_quarters: { rank: 2, title: 'Three quarters done', line: 'You have repaid 75% of what you borrowed.' },
  halfway: { rank: 1, title: 'Halfway there', line: 'More than half of this debt is repaid.' },
};

// Namespaced per user: two people signing in on the same device shouldn't
// suppress each other's celebrations.
const ackKey = (uid, m) => `2120_payoff_win:${uid || 'anon'}:${m.debt_id}:${m.stage}`;

function pickWin(uid, milestones) {
  const eligible = (milestones || []).filter((m) => STAGE[m.stage]);
  const unseen = eligible.filter((m) => {
    try { return !localStorage.getItem(ackKey(uid, m)); } catch { return true; }
  });
  if (unseen.length === 0) return null;
  return unseen.sort((a, b) => STAGE[b.stage].rank - STAGE[a.stage].rank)[0];
}

export function PayoffWinDialog({ milestones }) {
  const { user } = useAuth();
  const uid = user?.id;
  const win = useMemo(() => pickWin(uid, milestones), [uid, milestones]);
  const [open, setOpen] = useState(false);
  const [shared, setShared] = useState(false);

  useEffect(() => { setOpen(!!win); setShared(false); }, [win]);

  if (!win) return null;
  const copy = STAGE[win.stage];

  const acknowledge = () => {
    try { localStorage.setItem(ackKey(uid, win), new Date().toISOString()); } catch { /* private mode */ }
    setOpen(false);
  };

  const note = win.cleared
    ? `We've fully paid off the ${win.name}. One less debt!`
    : `${copy.title} on the ${win.name} — ${win.progress_pct}% repaid.`;

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: 'A milestone worth sharing', text: note });
      } else {
        await navigator.clipboard.writeText(note);
        toast.success('Copied — paste it to whoever is in this with you.');
      }
      setShared(true);
    } catch { /* dismissed */ }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : acknowledge())}>
      <DialogContent className="sm:max-w-md" data-testid="payoff-win-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <PartyPopper className="h-5 w-5 text-evergreen" aria-hidden="true" />
            <span data-testid="payoff-win-title">{copy.title}</span>
          </DialogTitle>
          <DialogDescription data-testid="payoff-win-name">
            {win.name} · {copy.line}
          </DialogDescription>
        </DialogHeader>

        {win.progress_pct != null && (
          <div className="mt-1">
            <div className="h-2 rounded-full bg-sage-light/60 overflow-hidden">
              <div
                className="h-full rounded-full bg-evergreen transition-[width] duration-700"
                style={{ width: `${Math.min(win.progress_pct, 100)}%` }}
                data-testid="payoff-win-progress"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {win.progress_pct}% repaid
              {win.paid_off_amount
                ? ` · ${formatMoney(win.paid_off_amount, win.currency)} paid down of ${formatMoney(win.original_principal, win.currency)}`
                : ''}
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-3">
          <Button onClick={share} data-testid="payoff-win-share">
            {shared ? <Check className="h-4 w-4 mr-1.5" /> : <Share2 className="h-4 w-4 mr-1.5" />}
            {shared ? 'Shared' : 'Share this win'}
          </Button>
          <Button variant="ghost" onClick={acknowledge} data-testid="payoff-win-dismiss">
            Just savour it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PayoffWinDialog;
